import { useMemo, useState } from "react";
import { MagnifyingGlassIcon, ArrowRightIcon, BoltIcon } from "@heroicons/react/24/solid";
import type { FeedPreview, FeedPreviewEpisode } from "@/api/client";
import SelectableEpisodeCard from "./SelectableEpisodeCard";
import { theme } from "@/lib/theme";
import { cn } from "@/lib/utils";

interface EpisodeSelectorProps {
  preview: FeedPreview;
  onStartSession: (
    episodeIndices: number[],
    episodeNumbers: number[]
  ) => Promise<void>;
  onChangeFeed: () => void;
  isLoading: boolean;
}

const MAX_EPISODES = 5;
const TOKEN_WARNING_THRESHOLD = 350000; // Warn at 350k tokens

function estimateTotalTokens(episodes: FeedPreviewEpisode[]): number {
  return episodes.reduce((sum, ep) => {
    if (ep.duration_seconds === null) return sum;
    // ~100 tokens per minute
    return sum + Math.round((ep.duration_seconds / 60) * 100);
  }, 0);
}

function formatTokenEstimate(tokens: number): string {
  if (tokens >= 1000) {
    return `~${(tokens / 1000).toFixed(0)}k`;
  }
  return `~${tokens}`;
}

function matchesSearch(episode: FeedPreviewEpisode, query: string): boolean {
  if (!query) return true;

  const words = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;

  const searchText = `${episode.title} ${episode.description ?? ""}`.toLowerCase();

  // ALL words must be present (AND intersection)
  return words.every((word) => searchText.includes(word));
}

export default function EpisodeSelector({
  preview,
  onStartSession,
  onChangeFeed,
  isLoading,
}: EpisodeSelectorProps) {
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");

  const selectedEpisodes = useMemo(() => {
    return preview.episodes.filter((ep) => selectedIndices.has(ep.index));
  }, [preview.episodes, selectedIndices]);

  // Check if all selected episodes have transcripts ready
  const allSelectedHaveTranscripts = useMemo(() => {
    if (selectedEpisodes.length === 0) return false;
    return selectedEpisodes.every((ep) => ep.has_transcript === true);
  }, [selectedEpisodes]);

  // Check if source allows instant sessions (cached or subscription)
  const canUseInstantSession = preview.source !== "feed" && allSelectedHaveTranscripts;

  const filteredUnselected = useMemo(() => {
    // Sort by published_date descending (newest first), nulls at end
    const sorted = [...preview.episodes].sort((a, b) => {
      if (!a.published_date && !b.published_date) return 0;
      if (!a.published_date) return 1;
      if (!b.published_date) return -1;
      return new Date(b.published_date).getTime() - new Date(a.published_date).getTime();
    });

    return sorted
      .filter((ep) => !selectedIndices.has(ep.index))
      .filter((ep) => matchesSearch(ep, searchQuery));
  }, [preview.episodes, selectedIndices, searchQuery]);

  const estimatedTokens = estimateTotalTokens(selectedEpisodes);
  const isOverWarningThreshold = estimatedTokens > TOKEN_WARNING_THRESHOLD;

  const toggleEpisode = (index: number) => {
    setSelectedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else if (next.size < MAX_EPISODES) {
        next.add(index);
      }
      return next;
    });
  };

  const handleStartSession = async () => {
    if (selectedIndices.size === 0 || isLoading) return;
    const indices = Array.from(selectedIndices);
    const numbers = selectedEpisodes
      .filter((ep) => ep.episode_number !== undefined)
      .map((ep) => ep.episode_number as number);
    await onStartSession(indices, numbers);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
            {preview.feed_title}
          </h1>
          {preview.feed_description && (
            <p className="text-gray-600 dark:text-gray-400 mt-1 line-clamp-2 max-w-2xl">
              {preview.feed_description}
            </p>
          )}
        </div>
        <button
          onClick={onChangeFeed}
          className="text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white underline underline-offset-2 whitespace-nowrap"
        >
          Change Feed
        </button>
      </div>

      {/* Search bar */}
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search episodes..."
          className={cn(
            "w-full pl-12 pr-4 py-3 rounded-xl",
            theme.borders.neubrutalism,
            theme.shadows.neubrutalismSm,
            "bg-white dark:bg-gray-800",
            "text-gray-900 dark:text-white",
            "placeholder:text-gray-400 dark:placeholder:text-gray-500",
            "focus:outline-none focus:ring-2",
            theme.ring.primaryOrange
          )}
        />
      </div>

      {/* Selected episodes section */}
      {selectedEpisodes.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              Selected ({selectedEpisodes.length} of {MAX_EPISODES})
            </h2>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {formatTokenEstimate(estimatedTokens)} tokens
              {!canUseInstantSession && ` · Estimated Cost: ~${selectedEpisodes.length * 3}¢`}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {selectedEpisodes.map((episode) => (
              <SelectableEpisodeCard
                key={episode.index}
                episode={episode}
                isSelected={true}
                onToggle={() => toggleEpisode(episode.index)}
                hasTranscript={episode.has_transcript}
              />
            ))}
          </div>

          {isOverWarningThreshold && (
            <p className="mt-3 text-sm text-amber-600 dark:text-amber-400">
              Note: Large context sizes may increase response time and cost.
            </p>
          )}
        </section>
      )}

      {/* All episodes section */}
      <section>
        <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
          {searchQuery ? "Search Results" : "All Episodes"} ({filteredUnselected.length})
        </h2>

        {filteredUnselected.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            {searchQuery
              ? "No episodes match your search"
              : "All episodes are selected"}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[50vh] overflow-y-auto pr-2">
            {filteredUnselected.map((episode) => (
              <SelectableEpisodeCard
                key={episode.index}
                episode={episode}
                isSelected={false}
                onToggle={() => toggleEpisode(episode.index)}
                disabled={selectedIndices.size >= MAX_EPISODES}
                hasTranscript={episode.has_transcript}
              />
            ))}
          </div>
        )}
      </section>

      {/* Start session button */}
      <div className="sticky bottom-4 pt-4">
        <button
          onClick={handleStartSession}
          disabled={selectedIndices.size === 0 || isLoading}
          className={cn(
            theme.buttons.primary,
            "w-full py-4 rounded-xl text-lg font-semibold",
            "flex items-center justify-center gap-3",
            "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
          )}
        >
          {isLoading ? (
            <>
              <span className="inline-block w-5 h-5 border-2 border-gray-900 border-t-transparent rounded-full animate-spin" />
              <span>Starting Session...</span>
            </>
          ) : canUseInstantSession ? (
            <>
              <BoltIcon className="w-5 h-5" />
              <span>
                Start Chat Instantly ({selectedIndices.size} episode
                {selectedIndices.size !== 1 ? "s" : ""})
              </span>
            </>
          ) : (
            <>
              <span>
                Start Session ({selectedIndices.size} episode
                {selectedIndices.size !== 1 ? "s" : ""})
              </span>
              <ArrowRightIcon className="w-5 h-5" />
            </>
          )}
        </button>
      </div>
    </div>
  );
}
